import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TableViewer from '../components/TableViewer';
import LoadingSpinner from '../shared/LoadingSpinner';
import { apiService } from '../services/api.service';

const TableViewerPage: React.FC = () => {
  const location = useLocation();
  const [tableData, setTableData] = useState<any>(null);
  const [endpoint, setEndpoint] = useState<string>('');
  const [reportTitle, setReportTitle] = useState<string>('');
  const [timestamp, setTimestamp] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Function to refetch data from API
  const refetchDataFromApi = async (endpointName: string) => {
    try {
      setError('');
      setLoading(true);
      console.log(`[TableViewerPage] Fetching data from API for endpoint: ${endpointName}`);
      
      // Extract filter parameters from URL (excluding navigation params)
      const urlParams = new URLSearchParams(window.location.search);
      const filterParams: { [key: string]: string } = {};
      
      const navigationParams = ['dataKey', 'endpoint', 'title', 'token', 'tanantId'];
      urlParams.forEach((value, key) => {
        // Exclude navigation/auth params, keep only data filter params
        if (!navigationParams.includes(key)) {
          filterParams[key] = value;
        }
      });
      
      console.log(`[TableViewerPage] Filter parameters from URL:`, filterParams);
      
      let response: any = null;
      
      // Map user-friendly endpoint names to API service method names
      // Keep in sync with the endpoint -> apiService mapping in ApiExplorer.tsx
      const endpointMap: { [key: string]: (params?: any) => Promise<any> } = {
        'Users Types': (params) => apiService.getUsersExcel(params),
        'Users Points': (params) => apiService.getFeeds(params),
        'Referral Users': (params) => apiService.getReferralSheet(params),
        'Referred Users': (params) => apiService.getReferredUsersSheet(params),
        'End Of Month': (params) => apiService.getEndOfMonthSheet(params),
        'Users': (params) => apiService.getUsersWithAcceptedBy(params),
        'Users Data': (params) => apiService.getUsersData(params),
        'Paymob': (params) => apiService.getPaymentLogs(params),
        'invoice-photos': (params) => apiService.getInvoicePhotosSheet(params),
        'Return System': (params) => apiService.getReturnSystemScanView(params),
        'Incentive': (params) => apiService.getReturnSystemSheet(params),
        'joining-requests': (params) => apiService.getJoiningRequests(params),
        'reset-logs': (params) => apiService.getResetLogs(params),
        'Deposit Amount Log': (params) => apiService.getDepositAmountOperations(params),
        'Users Points (QR Analysis)': (params) => apiService.getUserScanAnalysis(params),
        'User Details Per Product Per Action': (params) => apiService.getUserScanAnalysis(params),
        'Scans per product per user type': (params) => apiService.getScansDetailsPerUserType(params),
        'Scan Per Product details': (params) => apiService.getScanPerProductDetails(params),
        'Warehouse Scan In/Out': (params) => apiService.getWarehouseScanInOutDetails(params),
        'Deactivated Incentive QR': (params) => apiService.getDeactivatedIncentiveQR(params),
        'Configuration Result Per Product': (params) => apiService.getConfigurationSheetCounterPerProduct(params),
        'Warehouse Quantity Aggregation': (params) => apiService.getWarehouseQuantityAggregation(params),
        'Order Scanout From Warehouse': (params) => apiService.getOrderScanoutFromWarehouse(params),
        'Wallet Transactions': (params) => apiService.getWalletTransactionSheet(params),
        'Deliver Cash': (params) => apiService.getDeliverCash(params),
        'Deliver Transfer': (params) => apiService.getDeliverTransfer(params),
        'Welcome Bonus': (params) => apiService.getWelcomeBonus(params),
        'Gifts': (params) => apiService.getGiftsSheet(params),
      };
      
      // Get the API method for this endpoint
      const apiMethod = endpointMap[endpointName];
      
      if (apiMethod) {
        console.log(`[TableViewerPage] Calling API method for: ${endpointName} with params:`, filterParams);
        response = await apiMethod(filterParams);
      } else {
        // Fallback: try to construct an API path and use callExcelEndpoint
        console.warn(`[TableViewerPage] No direct method found for ${endpointName}, trying generic call`);
        const apiPath = `/${endpointName.replace(/\s+/g, '-').toLowerCase()}`;
        response = await apiService.callExcelEndpoint(apiPath, filterParams);
      }
      
      // Extract data from response
      const data = response?.data || response;
      console.log(`[TableViewerPage] API response received, data type:`, Array.isArray(data) ? 'array' : typeof data, 'records:', Array.isArray(data) ? data.length : '1');
      
      setTableData(data);
      setEndpoint(endpointName);
      setTimestamp(new Date().toISOString());
      setLoading(false);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to fetch data from API';
      console.error(`[TableViewerPage] Error fetching data:`, err);
      setError(errorMsg);
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dataKey = urlParams.get('dataKey');
    const endpointParam = urlParams.get('endpoint');
    const decodedEndpoint = endpointParam ? decodeURIComponent(endpointParam) : '';
    const titleParam = urlParams.get('title');
    const decodedTitle = titleParam ? decodeURIComponent(titleParam) : '';

    console.log(`[TableViewerPage] Mount: dataKey=${dataKey}, endpoint=${decodedEndpoint}`);

    // FIRST: Try to get data from URL parameter (localStorage - for fast small data)
    if (dataKey) {
      try {
        const storedData = localStorage.getItem(dataKey);
        if (storedData) {
          const parsedData = JSON.parse(storedData);

          // If data is embedded in localStorage, use it (small dataset)
          if (parsedData.data) {
            console.log(`[TableViewerPage] Found full data in localStorage`);
            setTableData(parsedData.data);
            setEndpoint(parsedData.endpoint || decodedEndpoint || '');
            setReportTitle(parsedData.title || decodedTitle || '');
            setTimestamp(parsedData.timestamp || new Date().toISOString());
            setLoading(false);
            localStorage.removeItem(dataKey);
            return;
          } else if (parsedData.isLargeData) {
            // Metadata only - large dataset, need to refetch from API
            console.log(`[TableViewerPage] Detected large dataset (metadata only), fetching from API`);
            localStorage.removeItem(dataKey);
            setReportTitle(parsedData.title || decodedTitle || '');
            if (decodedEndpoint) {
              refetchDataFromApi(decodedEndpoint);
              return;
            }
          }
        }
      } catch (error) {
        console.error('[TableViewerPage] Error loading from localStorage:', error);
      }
    }

    // SECOND: If endpoint is provided in URL, fetch from API
    if (decodedEndpoint) {
      console.log(`[TableViewerPage] Fetching from API: ${decodedEndpoint}`);
      setReportTitle(decodedTitle || '');
      refetchDataFromApi(decodedEndpoint);
      return;
    }

    // THIRD: Try to get data from location state (React Router)
    if (location.state) {
      const { data, endpoint: ep, title: reportTitleFromState, timestamp: ts } = location.state;
      console.log(`[TableViewerPage] Got data from location.state`);
      setTableData(data);
      setEndpoint(ep || '');
      setReportTitle(reportTitleFromState || '');
      setTimestamp(ts || new Date().toISOString());
      setLoading(false);
      return;
    }

    // FOURTH: Setup postMessage listener as fallback
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || tableData) return;
      
      if (event.data?.data) {
        console.log('[TableViewerPage] Received data via postMessage');
        setTableData(event.data.data);
        setEndpoint(event.data.endpoint || '');
        setReportTitle(event.data.title || '');
        setTimestamp(event.data.timestamp || new Date().toISOString());
        setLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);

    // Send ready message to parent window
    if (window.opener && !window.opener.closed) {
      try {
        console.log('[TableViewerPage] Sending ready signal to parent');
        window.opener.postMessage({ type: 'table-viewer-ready' }, '*');
      } catch (error) {
        console.error('[TableViewerPage] Error sending ready:', error);
      }
    }

    // Timeout fallback
    const timeoutId = setTimeout(() => {
      if (loading && !tableData) {
        console.log('[TableViewerPage] Loading timeout');
        setLoading(false);
        setError('Unable to load data. Please check the console for errors.');
      }
    }, 5000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeoutId);
    };
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center', 
        background: '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          backgroundColor: '#f5f5f5',
          borderRadius: '12px',
          padding: '3rem',
          maxWidth: '400px',
          color: '#000000',
          border: '1px solid #cccccc'
        }}>
          <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.8rem' }}>❌ Error</h2>
          <p style={{ margin: '0 0 1rem 0', opacity: 0.9, fontSize: '1rem' }}>
            {error}
          </p>
          <button
            onClick={() => window.close()}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              backgroundColor: '#000000',
              color: 'white',
              border: '1px solid #000000',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!tableData) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center', 
        background: '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          backgroundColor: '#f5f5f5',
          borderRadius: '12px',
          padding: '3rem',
          maxWidth: '400px',
          color: '#000000'
        }}>
          <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.8rem' }}>📊 No Data</h2>
          <p style={{ margin: '0 0 1rem 0', opacity: 0.9, fontSize: '1rem' }}>
            No data available to display
          </p>
          <button
            onClick={() => window.close()}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              backgroundColor: '#000000',
              color: 'white',
              border: '1px solid #000000',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <TableViewer
      data={tableData}
      endpoint={endpoint}
      reportTitle={reportTitle}
      timestamp={timestamp}
      showCloseButton={true}
    />
  );
};

export default TableViewerPage;
